import { useEffect, useState } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase } from './supabaseClient'

export type Role = 'external' | 'user' | 'trading' | 'admin' | 'affiliate' | 'owner'

const ROLE_WEIGHT: Record<Role, number> = {
  external: 0,
  user: 1,
  trading: 2,
  admin: 3,
  affiliate: 4,
  owner: 5,
}

export interface AuthSession {
  session: Session | null
  email: string | null
  role: Role | null
  loading: boolean
}

// Decode a Supabase JWT's payload without verifying the signature. Safe on
// the client because we only use this to *display* the role in the header —
// the actual authorization check happens on the API server with a verified
// signature.
function decodeRoleClaim(accessToken: string | undefined): Role | null {
  if (!accessToken) return null
  try {
    const payload = accessToken.split('.')[1]
    // base64url → base64
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(payload.length + (4 - payload.length % 4) % 4, '=')
    const json = atob(padded)
    const claims = JSON.parse(json) as { app_role?: string }
    if (
      claims.app_role === 'external'
      || claims.app_role === 'user'
      || claims.app_role === 'trading'
      || claims.app_role === 'admin'
      || claims.app_role === 'affiliate'
      || claims.app_role === 'owner'
    ) {
      return claims.app_role
    }
    return null
  }
  catch {
    return null
  }
}

/**
 * Subscribes to Supabase auth state. Returns the current session, the user's
 * email, the role decoded from the JWT's `app_role` claim (set server-side
 * by our custom_access_token_hook), and a loading flag during the first
 * `getSession()` call.
 */
export function useAuthSession(): AuthSession {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Pull the existing session synchronously from localStorage on mount.
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session)
      setLoading(false)
    })

    // Subscribe to future changes (sign in, sign out, token refresh).
    const { data: subscription } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next)
    })

    return () => subscription.subscription.unsubscribe()
  }, [])

  return {
    session,
    email: session?.user?.email ?? null,
    role: decodeRoleClaim(session?.access_token),
    loading,
  }
}

export function hasRole(role: Role | null, min: Role): boolean {
  if (!role) return false
  return ROLE_WEIGHT[role] >= ROLE_WEIGHT[min]
}

/** The more privileged of two roles — for gates that must never sit below another gate. */
export function higherRole(a: Role, b: Role): Role {
  return ROLE_WEIGHT[a] >= ROLE_WEIGHT[b] ? a : b
}
